/**
 * Copilot Panel Component
 * Conversational AI interface for Excel workbook interaction
 */

import React, { useState, useRef, useEffect } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorDisplay } from '../common/ErrorDisplay';
import {
  createChart,
  insertFormula,
  insertValue,
  writeValues,
  highlightRange,
  clearHighlight,
  undoLastWriterAction,
} from '../../office';
import { getSettings } from '../../store/settings.store';
import type {
  CopilotCommand,
  CopilotExecutionContext,
  CopilotActionResult,
  CopilotCommandPlan,
  CopilotIntent,
} from '@excel-ai-studio/shared';
import '../common/PreviewPanel.css';

interface CopilotMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actionResult?: CopilotActionResult;
}

interface CopilotPanelProps {
  context: CopilotExecutionContext;
  onCommandExecuted?: (result: CopilotActionResult) => void;
}

interface PendingModifyExecution {
  result: CopilotActionResult;
  preview: string;
}

interface PendingLearningFeedback {
  command: string;
  observedAnswer: string;
  actionType: CopilotIntent;
}

type ExecutionBudget = 'fast' | 'balanced' | 'reliable';

/**
 * Copilot Panel Component
 */
export const CopilotPanel: React.FC<CopilotPanelProps> = ({ context, onCommandExecuted }) => {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [pendingModify, setPendingModify] = useState<PendingModifyExecution | null>(null);
  const [pendingLearningFeedback, setPendingLearningFeedback] = useState<PendingLearningFeedback | null>(null);
  const [feedbackExpectedBehavior, setFeedbackExpectedBehavior] = useState('');
  const [feedbackSupervisedAutoApply, setFeedbackSupervisedAutoApply] = useState(true);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [debugMode, setDebugMode] = useState<boolean>(() => getSettings().copilotDebugMode);
  const [executionBudget, setExecutionBudget] = useState<ExecutionBudget>('balanced');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load suggested questions on mount
  useEffect(() => {
    loadSuggestedQuestions();
    setDebugMode(getSettings().copilotDebugMode);
  }, [context]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Load suggested questions from API
   */
  const loadSuggestedQuestions = async () => {
    try {
      const response = await fetch('/api/copilot/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestedQuestions(data.suggestions || []);
      }
    } catch (err) {
      console.error('Impossible de charger les suggestions:', err);
    }
  };

  const extractCommandPlan = (result: CopilotActionResult): CopilotCommandPlan | null => {
    if (result.plan) return result.plan;
    const payload = (result.data ?? null) as Record<string, unknown> | null;
    const maybePlan = payload?._plan as Record<string, unknown> | undefined;
    if (!maybePlan) return null;
    return {
      intent: String(maybePlan.intent ?? result.actionType) as CopilotCommandPlan['intent'],
      confidence: Number(maybePlan.confidence ?? 0),
      policy: {
        needsConfirmation: Boolean((maybePlan.policy as Record<string, unknown> | undefined)?.needsConfirmation),
        needsClarification: Boolean((maybePlan.policy as Record<string, unknown> | undefined)?.needsClarification),
        canAutoExecute: Boolean((maybePlan.policy as Record<string, unknown> | undefined)?.canAutoExecute),
        clarificationPrompt: String(
          ((maybePlan.policy as Record<string, unknown> | undefined)?.clarificationPrompt as string | undefined) ?? '',
        ) || undefined,
      },
      slots: (((maybePlan as Record<string, unknown>).slots as Record<string, unknown> | undefined) ?? {}),
    };
  };

  const getLearningActionType = (result: CopilotActionResult): CopilotIntent => {
    const plan = extractCommandPlan(result);
    if (plan?.intent) return plan.intent;
    return 'query';
  };

  const buildPlanSummary = (result: CopilotActionResult): string | null => {
    if (!debugMode) return null;
    const plan = extractCommandPlan(result);
    const payload = (result.data ?? {}) as Record<string, unknown>;
    const planV1 = (payload._planV1 && typeof payload._planV1 === 'object')
      ? (payload._planV1 as Record<string, unknown>)
      : null;
    if (!plan) return null;
    const confPct = Number.isFinite(plan.confidence) ? Math.round(plan.confidence * 100) : 0;
    const slotKeys = Object.keys(plan.slots || {}).filter((k) => !k.startsWith('_'));
    const slotLine = slotKeys.length > 0 ? slotKeys.join(', ') : 'aucun';
    if (planV1) {
      const mode = (planV1.mode as Record<string, unknown> | undefined)?.requested ?? 'execute';
      const intentDetected = (planV1.intent as Record<string, unknown> | undefined)?.detected ?? plan.intent;
      const operation = (planV1.operation as Record<string, unknown> | undefined)?.name ?? 'unknown';
      const decision = (planV1.policy as Record<string, unknown> | undefined)?.decision ?? 'execute_now';
      const willExecute = (planV1.execution as Record<string, unknown> | undefined)?.willExecuteWorkbookAction === true ? 'oui' : 'non';
      return `Plan:\n- Mode: ${String(mode)}\n- Intention métier: ${String(intentDetected)}\n- Operation: ${String(operation)}\n- Decision: ${String(decision)}\n- Executer Excel: ${willExecute}\n- Confiance: ${confPct}%\n- Confirmation: ${plan.policy.needsConfirmation ? 'oui' : 'non'}\n- Clarification: ${plan.policy.needsClarification ? 'oui' : 'non'}\n- Slots: ${slotLine}`;
    }
    return `Plan:\n- Intent: ${plan.intent}\n- Confiance: ${confPct}%\n- Confirmation: ${plan.policy.needsConfirmation ? 'oui' : 'non'}\n- Clarification: ${plan.policy.needsClarification ? 'oui' : 'non'}\n- Slots: ${slotLine}`;
  };

  const buildDebugJson = (result: CopilotActionResult): string | null => {
    if (!debugMode) return null;
    const plan = extractCommandPlan(result);
    const payload = (result.data ?? {}) as Record<string, unknown>;
    const provider = typeof payload.provider === 'string' ? payload.provider : 'n/a';
    const ruleMatch = (payload.match && typeof payload.match === 'object')
      ? (payload.match as Record<string, unknown>)
      : null;
    const planV1 = (payload._planV1 && typeof payload._planV1 === 'object')
      ? (payload._planV1 as Record<string, unknown>)
      : null;
    const debugObject = {
      intent: plan?.intent ?? result.actionType,
      confidence: plan?.confidence ?? null,
      policy: plan?.policy ?? null,
      slots: plan?.slots ?? null,
      provider,
      runtimeRuleMatch: ruleMatch,
      planV1,
      executionTimeMs: result.executionTime,
      actionType: result.actionType,
    };
    return JSON.stringify(debugObject, null, 2);
  };

  const executeModifyAction = async (result: CopilotActionResult): Promise<string | null> => {
    const payload = (result.data ?? null) as Record<string, unknown> | null;
    if (!payload) return null;

    const type = typeof payload.type === 'string' ? payload.type : '';
    const status = typeof payload.status === 'string' ? payload.status : '';
    if (status && status !== 'ready') {
      return null;
    }

    if (type === 'insert_formula') {
      const formula = typeof payload.formula === 'string' ? payload.formula.trim() : '';
      const address = typeof payload.address === 'string' ? payload.address.toUpperCase() : undefined;
      if (!formula) return null;
      await insertFormula(formula, undefined, address);
      return `✅ Formule insérée${address ? ` en ${address}` : ' dans la cellule active'}.`;
    }

    if (type === 'insert_value') {
      const value = payload.value as string | number | undefined;
      const address = typeof payload.address === 'string' ? payload.address.toUpperCase() : undefined;
      if (value === undefined || value === null) return null;
      await insertValue(value, address);
      return `✅ Valeur insérée${address ? ` en ${address}` : ' dans la cellule active'}.`;
    }

    if (type === 'write_values') {
      const values = payload.values as unknown[][] | undefined;
      if (!values || values.length === 0) return null;
      await writeValues(values);
      return '✅ Données écrites dans la feuille active.';
    }

    if (type === 'highlight') {
      const address = typeof payload.address === 'string' ? payload.address : '';
      if (!address) return null;
      const color = typeof payload.color === 'string' ? payload.color : undefined;
      await highlightRange(address, color);
      return `✅ Plage ${address} surlignée.`;
    }

    if (type === 'clear_highlight') {
      const address = typeof payload.address === 'string' ? payload.address : '';
      if (!address) return null;
      await clearHighlight(address);
      return `✅ Surlignage supprimé sur ${address}.`;
    }

    return null;
  };

  const createPivotTable = async (
    values: unknown[][],
    headers: string[] | undefined,
  ): Promise<string> => {
    const colNames =
      headers && headers.length > 0
        ? headers
        : (values[0] as unknown[]).map((v) => String(v ?? ''));
    const dataRows = headers && headers.length > 0 ? values : values.slice(1);

    if (dataRows.length === 0 || colNames.length === 0) {
      return 'Données insuffisantes pour créer un TCD.';
    }

    return Excel.run(async (ctx) => {
      const srcSheet = ctx.workbook.worksheets.getActiveWorksheet();
      srcSheet.load('name');
      const srcRange = srcSheet.getUsedRange();
      srcRange.load('address');
      await ctx.sync();

      const sheetLabel = `TCD_${srcSheet.name}`.substring(0, 31);
      const destSheet = ctx.workbook.worksheets.add(sheetLabel);
      const destCell = destSheet.getRange('A3');

      const pivotTable = destSheet.pivotTables.add(
        'PivotTable_Copilot',
        srcRange,
        destCell,
      );
      await ctx.sync();

      // Auto-configure: first text column as row, first numeric column as value
      const sampleRow = dataRows[0] as unknown[];
      let rowField: string | null = null;
      let valueField: string | null = null;

      for (let c = 0; c < colNames.length; c++) {
        const sample = sampleRow[c];
        if (!rowField && (typeof sample === 'string' || sample === null || sample === '')) {
          rowField = colNames[c];
        }
        if (!valueField && typeof sample === 'number') {
          valueField = colNames[c];
        }
        if (rowField && valueField) break;
      }

      if (rowField) {
        const rowHierarchy = pivotTable.hierarchies.getItemOrNullObject(rowField);
        await ctx.sync();
        if (!rowHierarchy.isNullObject) {
          pivotTable.rowHierarchies.add(rowHierarchy);
        }
      }
      if (valueField) {
        const valHierarchy = pivotTable.hierarchies.getItemOrNullObject(valueField);
        await ctx.sync();
        if (!valHierarchy.isNullObject) {
          pivotTable.dataHierarchies.add(valHierarchy);
        }
      }

      await ctx.sync();
      destSheet.activate();
      await ctx.sync();

      const parts = [`TCD créé sur la feuille "${sheetLabel}".`];
      if (rowField) parts.push(`Lignes : ${rowField}`);
      if (valueField) parts.push(`Valeurs : ${valueField}`);
      parts.push('Vous pouvez modifier les champs dans le volet TCD d\'Excel.');
      return parts.join('\n');
    });
  };

  const executeCreateAction = async (result: CopilotActionResult): Promise<string | null> => {
    const payload = (result.data ?? null) as Record<string, unknown> | null;
    if (!payload) return null;

    const type = typeof payload.type === 'string' ? payload.type : '';
    const values = (Array.isArray(payload.data) ? payload.data : context.selectedRange?.values) as unknown[][] | undefined;
    const headers = (
      Array.isArray(payload.headers) ? payload.headers : context.selectedRange?.headers
    ) as string[] | undefined;

    if (!values || values.length === 0) {
      return '⚠️ Aucune donnée disponible pour créer cet élément.';
    }

    if (type === 'dashboard') {
      // Dashboard creation now requires explicit user interaction in Dashboard page.
      // We redirect instead of auto-creating from chat to avoid silent/empty sheets.
      window.location.hash = 'dashboard';
      return [
        'ℹ️ Création interactive activée.',
        'Je vous ai redirigé vers l’onglet "Dashboard".',
        'Étapes: Lire la sélection → Générer le plan → choisir KPI/graphes → Créer.',
      ].join('\n');
    }

    if (type === 'report') {
      const { generateReportPlan } = await import('../../services/api.client');
      const { createReportSheet } = await import('../../services/report-creator.service');
      const response = await generateReportPlan({
        values,
        headers: headers && headers.length > 0
          ? headers
          : (values[0] as unknown[]).map((v) => String(v ?? '')),
      });
      const plan = response.plan;
      const created = await createReportSheet({
        plan,
        values,
        headers: headers && headers.length > 0
          ? headers
          : (values[0] as unknown[]).map((v) => String(v ?? '')),
        sheetName: plan.title || 'Rapport',
      });
      return created.success
        ? `✅ Rapport créé sur la feuille "${created.sheetName}".`
        : '⚠️ Création du rapport non confirmée.';
    }

    if (type === 'chart') {
      const filteredData = Array.isArray(payload.filteredData) ? payload.filteredData as unknown[][] : null;
      const chartType = typeof payload.chartType === 'string' ? payload.chartType : 'ColumnClustered';
      const chartTitle = typeof payload.chartTitle === 'string' ? payload.chartTitle : 'Graphique IA';

      if (filteredData && filteredData.length >= 2) {
        // Smart chart: write filtered data to a temp range, then chart from it
        try {
          const { writeFilteredDataAndChart } = await import('../../office/writer');
          await writeFilteredDataAndChart(filteredData, chartType, chartTitle);
          return `✅ ${chartTitle}`;
        } catch (chartErr) {
          const chartMsg = chartErr instanceof Error ? chartErr.message : String(chartErr);
          return `⚠️ Erreur creation graphique filtre : ${chartMsg}`;
        }
      }

      await createChart(chartType, chartTitle);
      return '✅ Graphique cree a partir de la selection active.';
    }

    if (type === 'pivot') {
      try {
        const pivotResult = await createPivotTable(values, headers);
        return pivotResult;
      } catch (pivotErr) {
        const pivotMsg = pivotErr instanceof Error ? pivotErr.message : String(pivotErr);
        return `Impossible de créer le TCD : ${pivotMsg}`;
      }
    }

    return null;
  };

  const buildModifyPreview = (result: CopilotActionResult): string | null => {
    const payload = (result.data ?? null) as Record<string, unknown> | null;
    if (!payload) return null;
    const type = typeof payload.type === 'string' ? payload.type : '';
    const status = typeof payload.status === 'string' ? payload.status : '';
    if (status && status !== 'ready') return null;

    if (type === 'insert_formula') {
      const formula = typeof payload.formula === 'string' ? payload.formula : '';
      const address = typeof payload.address === 'string' ? payload.address.toUpperCase() : 'cellule active';
      const planSummary = buildPlanSummary(result);
      return `${planSummary ? `${planSummary}\n` : ''}Action: insertion de formule\nCible: ${address}\nFormule: ${formula}`;
    }
    if (type === 'insert_value') {
      const address = typeof payload.address === 'string' ? payload.address.toUpperCase() : 'cellule active';
      const planSummary = buildPlanSummary(result);
      return `${planSummary ? `${planSummary}\n` : ''}Action: insertion de valeur\nCible: ${address}`;
    }
    if (type === 'write_values') {
      const planSummary = buildPlanSummary(result);
      return `${planSummary ? `${planSummary}\n` : ''}Action: écriture de données dans la feuille active.`;
    }
    if (type === 'highlight') {
      const address = typeof payload.address === 'string' ? payload.address.toUpperCase() : 'plage';
      const planSummary = buildPlanSummary(result);
      return `${planSummary ? `${planSummary}\n` : ''}Action: surlignage de ${address}`;
    }
    if (type === 'clear_highlight') {
      const address = typeof payload.address === 'string' ? payload.address.toUpperCase() : 'plage';
      const planSummary = buildPlanSummary(result);
      return `${planSummary ? `${planSummary}\n` : ''}Action: suppression du surlignage sur ${address}`;
    }
    return null;
  };

  const appendAssistantMessage = (content: string, actionResult?: CopilotActionResult) => {
    const message: CopilotMessage = {
      id: `msg-${Date.now()}-assistant`,
      type: 'assistant',
      content,
      timestamp: new Date(),
      actionResult,
    };
    setMessages((prev) => [...prev, message]);
  };

  const openFeedbackForMessage = (messageId: string) => {
    const idx = messages.findIndex((m) => m.id === messageId);
    if (idx < 0) return;
    const assistantMessage = messages[idx];
    if (assistantMessage.type !== 'assistant' || !assistantMessage.actionResult) return;
    let userCommand = '';
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].type === 'user') {
        userCommand = messages[i].content;
        break;
      }
    }
    if (!userCommand) return;
    setPendingLearningFeedback({
      command: userCommand,
      observedAnswer: assistantMessage.content,
      actionType: getLearningActionType(assistantMessage.actionResult),
    });
    setFeedbackExpectedBehavior('');
  };

  const submitLearningFeedback = async () => {
    if (!pendingLearningFeedback || !feedbackExpectedBehavior.trim()) return;
    setFeedbackSending(true);
    try {
      const response = await fetch('/api/copilot/learning/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: getSettings().provider,
          model: getSettings().model,
          apiKey: getSettings().apiKey,
          command: pendingLearningFeedback.command,
          observedAnswer: pendingLearningFeedback.observedAnswer.slice(0, 600),
          expectedBehavior: feedbackExpectedBehavior.trim(),
          actionType: pendingLearningFeedback.actionType,
          workbookName: context.workbookName,
          sheetName: context.sheetName,
          headers: context.selectedRange?.headers ?? [],
          supervisedAutoApply: feedbackSupervisedAutoApply,
        }),
      });
      if (!response.ok) {
        const bodyText = await response.text();
        throw new Error(`API error: ${response.status} ${response.statusText || ''} ${bodyText || ''}`.trim());
      }
      const result = await response.json() as {
        accepted: boolean;
        candidateId?: string;
        autoApplied?: boolean;
        message?: string;
      };
      appendAssistantMessage(
        result.message
          ? `🧠 Learning: ${result.message}`
          : `🧠 Feedback transmis${result.candidateId ? ` (candidate: ${result.candidateId})` : ''}.`,
      );
      const commandToRetry = pendingLearningFeedback.command;
      setPendingLearningFeedback(null);
      setFeedbackExpectedBehavior('');
      if (result.autoApplied) {
        appendAssistantMessage('🔁 Je relance automatiquement votre commande après correction...');
        void executeCommand(commandToRetry);
      }
    } catch (feedbackError) {
      appendAssistantMessage(
        `⚠️ Impossible d'envoyer le feedback learning: ${
          feedbackError instanceof Error ? feedbackError.message : 'Erreur inconnue'
        }`,
      );
    } finally {
      setFeedbackSending(false);
    }
  };

  const confirmPendingModify = async () => {
    if (!pendingModify) return;
    setIsLoading(true);
    try {
      const executionMessage = await executeModifyAction(pendingModify.result);
      appendAssistantMessage(
        executionMessage ?? 'ℹ️ Aucune exécution automatique disponible pour cette action.',
        pendingModify.result,
      );
    } catch (executionError) {
      const message = executionError instanceof Error ? executionError.message : 'Erreur inconnue';
      appendAssistantMessage(`⚠️ Échec de l\'exécution: ${message}`, pendingModify.result);
    } finally {
      setPendingModify(null);
      setIsLoading(false);
    }
  };

  const cancelPendingModify = () => {
    setPendingModify(null);
    appendAssistantMessage('❎ Modification annulée.');
  };

  const handleUndoLastAction = async () => {
    setError(null);
    try {
      const undone = await undoLastWriterAction();
      appendAssistantMessage(undone ? '↩️ Dernière action IA annulée.' : 'ℹ️ Aucune action IA à annuler.');
    } catch (undoError) {
      const message = undoError instanceof Error ? undoError.message : 'Erreur inconnue';
      appendAssistantMessage(`⚠️ Impossible d\'annuler: ${message}`);
    }
  };

  /**
   * Execute copilot command
   */
  const executeCommand = async (command: string) => {
    if (!command.trim()) return;

    setDebugMode(getSettings().copilotDebugMode);
    setError(null);
    setIsLoading(true);

    try {
      // Add user message
      const userMessage: CopilotMessage = {
        id: `msg-${Date.now()}`,
        type: 'user',
        content: command,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setInputValue('');

      // Execute command
      const response = await fetch('/api/copilot/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command,
          context,
          parameters: {
            executionBudget,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: CopilotActionResult = await response.json();
      const plan = extractCommandPlan(result);
      let createExecutionMessage: string | null = null;
      if (result.success && plan?.intent === 'create' && result.actionType === 'execute') {
        try {
          createExecutionMessage = await executeCreateAction(result);
        } catch (createErr) {
          createExecutionMessage = `⚠️ Échec de la création: ${
            createErr instanceof Error ? createErr.message : 'Erreur inconnue'
          }`;
        }
      }

      const preview = result.success && plan?.intent === 'modify' && result.actionType === 'execute'
        ? buildModifyPreview(result)
        : null;
      const clarificationHint =
        !preview && plan?.policy.needsClarification
          ? `\n\nClarification requise: ${plan.policy.clarificationPrompt ?? 'Précisez la cible et la plage.'}`
          : '';
      const planSummary = !preview ? buildPlanSummary(result) : null;
      const debugJson = !preview ? buildDebugJson(result) : null;

      // Add assistant message
      const assistantMessage: CopilotMessage = {
        id: `msg-${Date.now()}-response`,
        type: 'assistant',
        content: preview
          ? `${result.message}\n\nPrévisualisation (dry-run):\n${preview}\n\nConfirmer la modification ?`
          : createExecutionMessage
          ? `${result.message}\n\n${createExecutionMessage}`
          : `${result.message}${clarificationHint}${planSummary ? `\n\n${planSummary}` : ''}${debugJson ? `\n\nPourquoi Copilot a choisi cette action:\n${debugJson}` : ''}`,
        timestamp: new Date(),
        actionResult: result,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      if (preview) {
        setPendingModify({ result, preview });
      }

      // Notify parent
      if (onCommandExecuted) {
        onCommandExecuted(result);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);

      // Add error message
      const errorMessage_: CopilotMessage = {
        id: `msg-${Date.now()}-error`,
        type: 'assistant',
        content: `Erreur: ${errorMessage}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage_]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle suggested question click
   */
  const handleSuggestedQuestion = (question: string) => {
    executeCommand(question);
  };

  /**
   * Handle input key press
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      executeCommand(inputValue);
    }
  };

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <h3>🤖 Excel Copilot</h3>
        <p className="preview-subtitle">Demandez-moi n'importe quoi sur vos données</p>
      </div>

      {error && <ErrorDisplay message={error} onDismiss={() => setError(null)} />}

      {/* Messages Container */}
      <div className="copilot-messages" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            <p>👋 Bonjour! Je suis votre Copilot Excel.</p>
            <p>Demandez-moi d'analyser, créer ou modifier vos données.</p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              marginBottom: '12px',
              padding: '10px',
              borderRadius: '8px',
              backgroundColor: msg.type === 'user' ? '#e3f2fd' : '#f5f5f5',
              borderLeft: `4px solid ${msg.type === 'user' ? '#2196F3' : '#999'}`,
            }}
          >
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              {msg.type === 'user' ? '👤 Vous' : '🤖 Copilot'}
            </div>
            <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
            {msg.actionResult && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                <strong>Action:</strong> {msg.actionResult.actionType} ({msg.actionResult.executionTime}ms)
              </div>
            )}
            {msg.type === 'assistant' && msg.actionResult && (
              <div style={{ marginTop: '8px' }}>
                <button
                  onClick={() => openFeedbackForMessage(msg.id)}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #bbb',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Signaler une mauvaise interpretation
                </button>
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div style={{ padding: '10px' }}>
            <LoadingSpinner label="Traitement en cours..." />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Questions */}
      {messages.length === 0 && suggestedQuestions.length > 0 && (
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            <strong>Questions suggérées:</strong>
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {suggestedQuestions.slice(0, 4).map((question, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestedQuestion(question)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '13px',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#e0e0e0';
                }}
                onMouseOut={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f0f0f0';
                }}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      {pendingModify && (
        <div
          style={{
            marginTop: '12px',
            padding: '10px',
            border: '1px solid #f0c36d',
            borderRadius: '8px',
            background: '#fff9e6',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div style={{ fontSize: '12px', color: '#555', whiteSpace: 'pre-wrap' }}>
            <strong>Confirmation requise</strong>
            {'\n'}
            {pendingModify.preview}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={confirmPendingModify}
              disabled={isLoading}
              style={{
                padding: '8px 12px',
                backgroundColor: '#2e7d32',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              Confirmer
            </button>
            <button
              onClick={cancelPendingModify}
              disabled={isLoading}
              style={{
                padding: '8px 12px',
                backgroundColor: '#b71c1c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      {pendingLearningFeedback && (
        <div
          style={{
            marginTop: '12px',
            padding: '10px',
            border: '1px solid #b39ddb',
            borderRadius: '8px',
            background: '#f5f0ff',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div style={{ fontSize: '12px', color: '#444' }}>
            <strong>Surveillance Learning</strong>
            <div>Commande: {pendingLearningFeedback.command}</div>
          </div>
          <textarea
            value={feedbackExpectedBehavior}
            onChange={(e) => setFeedbackExpectedBehavior(e.target.value)}
            placeholder="Comportement attendu (ex: Lister les colonnes du tableau)"
            style={{
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '13px',
              lineHeight: '1.35',
              resize: 'vertical',
              whiteSpace: 'pre-wrap',
            }}
            rows={3}
            wrap="soft"
          />
          <label style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              checked={feedbackSupervisedAutoApply}
              onChange={(e) => setFeedbackSupervisedAutoApply(e.target.checked)}
            />
            Appliquer automatiquement en mode supervise
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={submitLearningFeedback}
              disabled={feedbackSending || !feedbackExpectedBehavior.trim()}
              style={{
                padding: '8px 12px',
                backgroundColor: '#673ab7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: feedbackSending || !feedbackExpectedBehavior.trim() ? 'not-allowed' : 'pointer',
                opacity: feedbackSending || !feedbackExpectedBehavior.trim() ? 0.7 : 1,
              }}
            >
              {feedbackSending ? 'Envoi...' : 'Corriger avec IA'}
            </button>
            <button
              onClick={() => setPendingLearningFeedback(null)}
              disabled={feedbackSending}
              style={{
                padding: '8px 12px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: feedbackSending ? 'not-allowed' : 'pointer',
                opacity: feedbackSending ? 0.7 : 1,
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Demandez-moi n'importe quoi..."
          disabled={isLoading}
          rows={5}
          wrap="soft"
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            lineHeight: '1.35',
            resize: 'vertical',
            whiteSpace: 'pre-wrap',
            overflowY: 'auto',
            minHeight: 105,
          }}
        />

        {/* Row actions: Mode à gauche, boutons en dessous de la saisie */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 160 }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Mode</label>
            <select
              value={executionBudget}
              onChange={(e) => setExecutionBudget(e.target.value as ExecutionBudget)}
              disabled={isLoading || !!pendingModify}
              style={{
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '12px',
                backgroundColor: '#fff',
              }}
            >
              <option value="fast">Rapide</option>
              <option value="balanced">Equilibre</option>
              <option value="reliable">Fiable</option>
            </select>
          </div>

          <button
            onClick={handleUndoLastAction}
            disabled={isLoading}
            style={{
              padding: '8px 12px',
              backgroundColor: '#616161',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              fontSize: 13,
              lineHeight: '1.2',
            }}
          >
            Annuler IA
          </button>

          <button
            onClick={() => executeCommand(inputValue)}
            disabled={isLoading || !inputValue.trim() || !!pendingModify}
            style={{
              padding: '8px 12px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
              opacity: isLoading || !inputValue.trim() ? 0.6 : 1,
              fontSize: 13,
              lineHeight: '1.2',
              flex: 1,
            }}
          >
            Envoyer
          </button>
        </div>
      </div>

      {/* Context Info */}
      <div style={{ marginTop: '12px', fontSize: '11px', color: '#999' }}>
        <p>
          📊 Feuille: <strong>{context.sheetName}</strong>
        </p>
        {context.selectedRange && (
          <p>
            📍 Plage: <strong>{context.selectedRange.address}</strong> ({context.selectedRange.values.length} lignes)
          </p>
        )}
      </div>
    </div>
  );
};
